import { Button, ButtonV2, SkeletonLoader, Spinner, TextArea, TooltipV2 } from '@cimplico/components';
import { LinkSlashIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useWorkpaperContext } from '../../../../../providers/WorkpaperProvider';
import { useCreateWorksheetEntry } from '../../../../../queries/use-create-worksheet-entry';
import { CWTSchemaPropertySummary, QuickAction, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { WorksheetTypeEnum, HandleLinkedResourceAction, HandleLinkedResourceType } from '../../../../../types/worksheet';
import { selectCalculation, selectRenamingListItem, selectWorksheet } from '../../store/selectors';
import { useHandleLinkedResource } from '../../../../../queries/use-handle-linked-resource';
import { toastNotification } from '../../../../../components/toast/Toast';
import LinkEntryForm from '../../../calculations/LinkEntryForm';
import queryClient from '../../../../../query-client';
import { useWorksheet } from '../../../../../queries/use-worksheet';

type QuickActionsProps = {
    actions?: QuickAction[];
    field: WorksheetSchemaField | CWTSchemaPropertySummary;
};

function QuickActions({ field, actions = [] }: QuickActionsProps): JSX.Element {
    const renamingPropertyId = useSelector(selectRenamingListItem);
    const calculation = useSelector(selectCalculation);
    const worksheet = useSelector(selectWorksheet);
    const workpaper = useWorkpaperContext();
    const handleLinkedResourceQuery = useHandleLinkedResource(worksheet?.id, calculation.id);
    const entryQuery = useCreateWorksheetEntry(worksheet?.id ?? '');
    const params = useParams();

    const isDisabled = renamingPropertyId !== undefined;
    const linkedEntry = field.linkedResources?.find((lr) => lr.type === 'ENTRY');
    const worksheetQuery = useWorksheet(linkedEntry?.metadata.worksheetId);
    const worksheetName = worksheetQuery.data?.worksheet.name;

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    function handlePreviewClick(): void {
        if (linkedEntry?.metadata?.worksheetId) {
            if (location.pathname.includes('/calculations/')) {
                setSearchParams((prev) => {
                    prev.set('worksheetPreview', linkedEntry.metadata.worksheetId);
                    return prev;
                });
            } else {
                // If NOT, navigate normally
                navigate(
                    `/${params.firmShortId}/workpapers/${params.workpaperId}/trial-balance/${linkedEntry.metadata.worksheetId}?tab=entries`
                );
            }
        }
    }
    function unlinkEntry(entryId: string, fieldId: string): void {
        handleLinkedResourceQuery.mutate(
            {
                action: HandleLinkedResourceAction.DELETE,
                propertyId: fieldId,
                resourceType: HandleLinkedResourceType.ENTRY,
                resourceId: entryId,
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Entry successfully unlinked from the calculation.');
                },
                onError: () => {
                    toastNotification('error', 'Could not unlink entry from the calculation.');
                },

                onSettled: () => {
                    queryClient.invalidateQueries(['workpapers', workpaper.id, 'calculations', calculation.id]);
                },
            }
        );
    }
    if (actions.length === 0) {
        return <></>;
    }
    if (worksheet && worksheet.type !== WorksheetTypeEnum.RECONCILIATION) {
        return <></>;
    }
    return (
        <div className="flex items-center">
            {!linkedEntry ? (
                <>
                    {entryQuery.isLoading && (
                        <div className="mr-2 h-8 flex items-center">
                            <Spinner size="sm" />
                        </div>
                    )}
                    {!entryQuery.isLoading && (
                        <TooltipV2 message="Create an entry" position="bottom" disabled={isDisabled}>
                            <LinkEntryForm
                                description={actions[0].payload.defaultDescription}
                                date={actions[0].payload.defaultDate}
                                value={field.value as number}
                                name={field.name}
                                nature={actions[0].payload.positiveNature}
                                workpaperEnd={workpaper.periodEnd}
                                calcId={calculation.id}
                                fieldId={field.id}
                                defaultWorksheet={worksheet}
                            />
                        </TooltipV2>
                    )}
                </>
            ) : (
                <>
                    <TooltipV2 message="Unlink this entry" position="bottom" disabled={isDisabled}>
                        <ButtonV2
                            size="sm"
                            type="hollow"
                            leftIcon={<LinkSlashIcon className="h-4 w-4" />}
                            onClick={(): void => unlinkEntry(linkedEntry.id, field.id)}
                            isDisabled={isDisabled}
                            disabledHint="Please finish naming the list item"
                        />
                    </TooltipV2>
                    {worksheetQuery.isLoading ? (
                        <SkeletonLoader height="24px" width="150px" />
                    ) : (
                        <button className="text-indigo-600 text-sm  " onClick={handlePreviewClick}>
                            {worksheetName}
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

export default QuickActions;
